// useClubTasks — the action-oriented hub (FC 25 Task List / FM26
// Portal pattern): derives a prioritized list of pending decisions
// from existing game state, each with a direct action or deep-link.

import { useMemo } from 'react'
import { useGameStore } from '@/store/gameStore'
import { getNextLevel } from '@/config/clubLevelConfig'
import { getRepairCost, repairCourt } from '@/game/systems/MaintenanceSystem'
import { TOURNAMENT_DEFINITIONS } from '@/config/tournamentConfig'
import { SEASON, PLAYER_CLUB_ID, DIVISIONS } from '@/config/leagueConfig'
import { ROSTER_RULES, getRosterCap } from '@/config/playersConfig'

export interface ClubTask {
  id: string
  priority: 1 | 2 | 3 // 1 = urgent
  icon: string // Lucide icon key resolved by TaskCard
  title: string
  detail: string
  action?: { label: string; run: () => void; cost?: number; disabled?: boolean }
  link?: { label: string; to: string }
}

export function useClubTasks(): ClubTask[] {
  const courts = useGameStore(s => s.courts)
  const money = useGameStore(s => s.money)
  const tournamentOffers = useGameStore(s => s.tournamentOffers)
  const activeTournament = useGameStore(s => s.activeTournament)
  const transferMarket = useGameStore(s => s.transferMarket)
  const players = useGameStore(s => s.players)
  const pendingEvent = useGameStore(s => s.pendingEvent)
  const league = useGameStore(s => s.league)
  const week = useGameStore(s => s.week)
  const staff = useGameStore(s => s.staff)
  const daysInDebt = useGameStore(s => s.daysInDebt)
  const clubLevel = useGameStore(s => s.clubLevel)
  const buildings = useGameStore(s => s.buildings)
  const reputation = useGameStore(s => s.reputation)
  const enabledServices = useGameStore(s => s.enabledServices)
  const courtUpgrades = useGameStore(s => s.courtUpgrades)
  const memberCounts = useGameStore(s => s.memberCounts)

  return useMemo(() => {
    const tasks: ClubTask[] = []

    // Debt warning (most urgent)
    if (money < 0) {
      tasks.push({
        id: 'debt',
        priority: 1,
        icon: 'siren',
        title: `Cuenta en numeros rojos (${daysInDebt} dia${daysInDebt === 1 ? '' : 's'})`,
        detail: 'Con 14 dias seguidos en negativo el club quiebra',
        link: { label: 'Finanzas', to: '/economy' },
      })
    }

    // Damaged courts -> direct repair
    courts
      .filter(c => c.condition < 60)
      .sort((a, b) => a.condition - b.condition)
      .slice(0, 2)
      .forEach((court, index) => {
        const cost = getRepairCost(court.condition, court.type)
        tasks.push({
          id: `repair-${court.id}`,
          priority: court.condition < 30 ? 1 : 2,
          icon: 'wrench',
          title: `Pista al ${Math.round(court.condition)}% de condicion`,
          detail: court.condition < 30
            ? 'Los clientes estan cancelando reservas'
            : 'El desgaste reduce la satisfaccion',
          action: {
            label: `Reparar · ${cost.toLocaleString('es-ES')}€`,
            run: () => repairCourt(court.id),
            cost,
            disabled: money < cost,
          },
        })
        void index
      })

    // Pending event decision
    if (pendingEvent) {
      tasks.push({
        id: 'pending-event',
        priority: 1,
        icon: 'zap',
        title: 'Evento esperando tu decision',
        detail: 'Hay una situacion del club sin resolver',
        link: { label: 'Resolver', to: '/dashboard' },
      })
    }

    // Tournament offers
    tournamentOffers.forEach(type => {
      const def = TOURNAMENT_DEFINITIONS[type]
      tasks.push({
        id: `tournament-${type}`,
        priority: 2,
        icon: 'trophy',
        title: `Oferta: ${def.name}`,
        detail: `+${def.reputationBonus} reputacion al completarlo`,
        link: { label: 'Ver oferta', to: '/competitions' },
      })
    })

    // League: next matchday
    if (league && league.phase !== 'finished') {
      const next = league.fixtures.find(
        f => !f.result && (f.homeId === PLAYER_CLUB_ID || f.awayId === PLAYER_CLUB_ID)
      )
      if (next) {
        const rival = league.clubs.find(
          c => c.id === (next.homeId === PLAYER_CLUB_ID ? next.awayId : next.homeId)
        )
        const matchdayWeek = league.seasonStartWeek + next.matchday
        const weeksLeft = matchdayWeek - week
        tasks.push({
          id: `league-md-${next.matchday}`,
          priority: weeksLeft <= 0 ? 2 : 3,
          icon: 'shield',
          title: `J${next.matchday} de liga vs ${rival?.name ?? 'Rival'}`,
          detail: weeksLeft <= 0
            ? `Se juega esta semana · prima ${DIVISIONS[league.division].matchdayWinBonus}€`
            : `En ${weeksLeft} semana${weeksLeft === 1 ? '' : 's'}`,
          link: { label: 'Liga', to: '/competitions' },
        })
      }
    }

    // League enrollment available
    if (!league && clubLevel >= 3 && players.length >= ROSTER_RULES.leagueMinPlayers) {
      tasks.push({
        id: 'league-enroll',
        priority: 2,
        icon: 'shield',
        title: 'Puedes inscribirte en la liga',
        detail: `${SEASON.matchdays} jornadas, primas por victoria y ascensos`,
        link: { label: 'Inscribirse', to: '/competitions' },
      })
    }

    // Transfer market candidates
    if (transferMarket.length > 0 && players.length < getRosterCap(clubLevel)) {
      const best = [...transferMarket].sort((a, b) => b.level - a.level)[0]
      tasks.push({
        id: 'market',
        priority: 3,
        icon: 'users',
        title: `${transferMarket.length} candidatos en el mercado`,
        detail: `El mejor: nivel ${best.level} por ${best.signingFee.toLocaleString('es-ES')}€`,
        link: { label: 'Mercado', to: '/staff' },
      })
    }

    // Unhappy staff
    const unhappy = staff.filter(s => s.happiness < 40)
    if (unhappy.length > 0) {
      tasks.push({
        id: 'staff-morale',
        priority: 2,
        icon: 'frown',
        title: `${unhappy.length} empleado${unhappy.length === 1 ? '' : 's'} con moral baja`,
        detail: 'La moral baja reduce su rendimiento; sanea las finanzas',
        link: { label: 'Personal', to: '/staff' },
      })
    }

    // Next level within reach (all requirements met is auto, so show when close)
    const nextLevel = getNextLevel(clubLevel)
    if (nextLevel) {
      const checks = nextLevel.requirements.map(req => {
        switch (req.type) {
          case 'courts': return courts.length >= req.value
          case 'buildings': return buildings.length >= req.value
          case 'staff': return staff.length >= req.value
          case 'reputation': return reputation >= req.value
          case 'services': return enabledServices.length >= req.value
          case 'upgrades': return Object.values(courtUpgrades).flat().length >= req.value
          case 'members': return Object.values(memberCounts).reduce((s, n) => s + n, 0) >= req.value
          default: return false
        }
      })
      const missing = checks.filter(c => !c).length
      if (missing === 1) {
        const pendingReq = nextLevel.requirements[checks.findIndex(c => !c)]
        tasks.push({
          id: 'level-close',
          priority: 3,
          icon: 'target',
          title: `A un paso de ${nextLevel.name}`,
          detail: `Solo falta: ${pendingReq.label}`,
          link: { label: 'Instalaciones', to: '/facilities' },
        })
      }
    }

    // Active tournament in progress
    if (activeTournament?.status === 'in_progress') {
      tasks.push({
        id: 'tournament-live',
        priority: 3,
        icon: 'trophy',
        title: `${activeTournament.name} en juego`,
        detail: 'El cuadro avanza cada dia',
        link: { label: 'Ver cuadro', to: '/competitions' },
      })
    }

    return tasks.sort((a, b) => a.priority - b.priority)
  }, [
    courts, money, tournamentOffers, activeTournament, transferMarket, players,
    pendingEvent, league, week, staff, daysInDebt, clubLevel, buildings,
    reputation, enabledServices, courtUpgrades, memberCounts,
  ])
}
